import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ticketsAPI } from '../lib/api';
import { LUGARES, CANALES_RESPUESTA, TIPOS_ENSER, ELEMENTOS_COMUNES, MAX_ITEMS_PER_TICKET, TURNO_RECOGIDA } from '../lib/constants';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { AlertTriangle, Calendar, CalendarDays, CheckCircle } from 'lucide-react';

const formatFechaISO = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const CitizenWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [diaCompleto, setDiaCompleto] = useState(null); // { message, zona, fecha, next_available }
  const [diasNoDisponibles, setDiasNoDisponibles] = useState({ completos: [], festivos: [] });
  const [loadingDias, setLoadingDias] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Contacto
    nombre_ciudadano: '',
    telefono: '',
    email: '',
    canal_respuesta_preferido: 'Email',
    consentimiento_notificaciones: true,
    
    // Step 2: Ubicación
    direccion: '',
    bloque: '',
    piso: '',
    lugar: 'Casco Antiguo',
    coordenadas: null,
    
    // Step 3: Fecha (no turno selection - fixed schedule)
    fecha_preferida_retirada: '',
    
    // Step 4: Items
    items: [{ tipo_enser: 'Muebles/Enseres', elemento: '', datos_item: '' }],
    
    // Step 5: Observaciones
    observaciones: ''
  });

  const totalSteps = 5;

  useEffect(() => {
    if (currentStep === 3 && formData.lugar) {
      setLoadingDias(true);
      ticketsAPI.capacidadRango(formData.lugar, 90)
        .then(res => {
          setDiasNoDisponibles({
            completos: res.data.dias_completos || [],
            festivos: res.data.festivos || []
          });
        })
        .catch(() => {
          setDiasNoDisponibles({ completos: [], festivos: [] });
        })
        .finally(() => setLoadingDias(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formData.lugar]);

  const esDiaBloqueado = (date) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    if (date < hoy) return true;
    const diaSemana = date.getDay();
    if (diaSemana === 0 || diaSemana === 6) return true;
    const fechaStr = formatFechaISO(date);
    return diasNoDisponibles.completos.includes(fechaStr) || diasNoDisponibles.festivos.includes(fechaStr);
  };

  const esFestivoSeleccionado = (fechaStr) => diasNoDisponibles.festivos.includes(fechaStr);
  const esDiaCompletoSeleccionado = (fechaStr) => diasNoDisponibles.completos.includes(fechaStr);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    if (formData.items.length < MAX_ITEMS_PER_TICKET) {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, { tipo_enser: 'Muebles/Enseres', elemento: '', datos_item: '' }]
      }));
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.nombre_ciudadano || !formData.telefono || !formData.email) {
          toast.error('Por favor, complete todos los campos obligatorios');
          return false;
        }
        if (!/^[+]?[\d\s-]+$/.test(formData.telefono)) {
          toast.error('Formato de teléfono inválido');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error('Formato de email inválido');
          return false;
        }
        return true;
      
      case 2:
        if (!formData.direccion || !formData.lugar) {
          toast.error('Por favor, indique su dirección y lugar');
          return false;
        }
        return true;
      
      case 3:
        if (!formData.fecha_preferida_retirada) {
          toast.error('Por favor, seleccione una fecha');
          return false;
        }
        const selectedDate = new Date(formData.fecha_preferida_retirada + 'T12:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          toast.error('La fecha no puede ser anterior a hoy');
          return false;
        }
        const dayOfWeek = selectedDate.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          toast.error('No hay servicio de recogida los sábados y domingos. Por favor, seleccione un día laborable.');
          return false;
        }
        if (esFestivoSeleccionado(formData.fecha_preferida_retirada)) {
          toast.error('Esa fecha es festiva y no hay servicio de recogida. Por favor, elija otro día.');
          return false;
        }
        if (esDiaCompletoSeleccionado(formData.fecha_preferida_retirada)) {
          toast.error('Ese día ya está completo para su zona. Por favor, elija otro día.');
          return false;
        }
        return true;
      
      case 4:
        if (formData.items.length === 0) {
          toast.error('Debe agregar al menos 1 item');
          return false;
        }
        for (let item of formData.items) {
          if (!item.elemento) {
            toast.error('Todos los items deben tener un elemento especificado');
            return false;
          }
        }
        return true;
      
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        fecha_preferida_retirada: new Date(formData.fecha_preferida_retirada).toISOString(),
        turno_recogida: '07:00-13:00',
        procedencia: 'Plataforma Digital'
      };

      const response = await ticketsAPI.create(payload);
      toast.success(`¡Solicitud creada! Ticket: ${response.data.ticket_id}`);
      setTimeout(() => {
        navigate(`/consulta?ticket=${response.data.ticket_id}`);
      }, 2000);

    } catch (error) {
      console.error('Error creating ticket:', error);
      const detail = error.response?.data?.detail;

      if (error.response?.status === 409 && detail?.error === 'dia_completo') {
        // Show day-full UI instead of generic toast
        setDiaCompleto(detail);
      } else {
        toast.error(detail?.message || detail || 'Error al crear la solicitud');
      }
    } finally {
      setLoading(false);
    }
  };

  // When citizen picks an alternative date from the blocking screen
  const handleSelectAlternativeDate = (fecha) => {
    updateFormData('fecha_preferida_retirada', fecha);
    setDiaCompleto(null);
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Día completo — pantalla de alternativas */}
      {diaCompleto && (
        <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 shadow-sm" data-testid="dia-completo-panel">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900 text-lg">Día completo</h3>
              <p className="text-sm text-amber-800 mt-1">{diaCompleto.message}</p>
            </div>
          </div>
          {diaCompleto.next_available?.length > 0 ? (
            <>
              <p className="text-sm font-semibold text-amber-900 mb-3">Elige una fecha disponible:</p>
              <div className="flex flex-wrap gap-3">
                {diaCompleto.next_available.map(d => (
                  <button
                    key={d.fecha}
                    onClick={() => handleSelectAlternativeDate(d.fecha)}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white border-2 border-teal-300 hover:border-teal-500 hover:bg-teal-50 transition-all shadow-sm text-left"
                    data-testid={`alt-date-${d.fecha}`}
                  >
                    <Calendar className="w-4 h-4 text-teal-600" />
                    <div>
                      <p className="font-semibold text-teal-800 capitalize">{d.label}</p>
                      <p className="text-xs text-teal-600">{d.disponibles} plazas libres</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-amber-800 font-medium">No hay disponibilidad en los próximos 60 días laborables. Contacta con el servicio municipal.</p>
          )}
          <button
            onClick={() => setDiaCompleto(null)}
            className="mt-4 text-xs text-amber-700 underline hover:no-underline"
          >
            Volver al formulario
          </button>
        </div>
      )}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700" data-testid="wizard-step-indicator">
            Paso {currentStep} de {totalSteps}
          </span>
          <span className="text-sm text-gray-500">
            {Math.round((currentStep / totalSteps) * 100)}% completado
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: ((currentStep / totalSteps) * 100) + '%' }}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {currentStep === 1 && 'Información de Contacto'}
            {currentStep === 2 && 'Ubicación'}
            {currentStep === 3 && 'Fecha de Recogida'}
            {currentStep === 4 && 'Items a Recoger'}
            {currentStep === 5 && 'Resumen y Envío'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Contacto */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="nombre">Nombre Completo *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre_ciudadano}
                  onChange={(e) => updateFormData('nombre_ciudadano', e.target.value)}
                  placeholder="Ej: Juan Pérez García"
                  data-testid="input-nombre"
                />
              </div>
              <div>
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => updateFormData('telefono', e.target.value)}
                  placeholder="Ej: +34612345678"
                  data-testid="input-telefono"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="Ej: juan@example.com"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label>Canal de Respuesta Preferido *</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {CANALES_RESPUESTA.map(canal => (
                    <button
                      key={canal.value}
                      type="button"
                      onClick={() => updateFormData('canal_respuesta_preferido', canal.value)}
                      className={`p-3 border rounded-lg text-sm font-medium transition-colors duration-150 ${
                        formData.canal_respuesta_preferido === canal.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`btn-canal-${canal.value}`}
                    >
                      <div className="text-2xl mb-1">{canal.icon}</div>
                      {canal.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="consentimiento"
                  checked={formData.consentimiento_notificaciones}
                  onChange={(e) => updateFormData('consentimiento_notificaciones', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="consentimiento" className="font-normal">
                  Acepto recibir notificaciones sobre el estado de mi solicitud
                </Label>
              </div>
            </div>
          )}

          {/* Step 2: Ubicación */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="direccion">Dirección Completa *</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => updateFormData('direccion', e.target.value)}
                  placeholder="Ej: Calle Real 45"
                  data-testid="input-direccion"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bloque">Bloque (opcional)</Label>
                  <Input
                    id="bloque"
                    value={formData.bloque}
                    onChange={(e) => updateFormData('bloque', e.target.value)}
                    placeholder="Ej: A"
                  />
                </div>
                <div>
                  <Label htmlFor="piso">Piso (opcional)</Label>
                  <Input
                    id="piso"
                    value={formData.piso}
                    onChange={(e) => updateFormData('piso', e.target.value)}
                    placeholder="Ej: 2B"
                  />
                </div>
              </div>
              <div>
                <Label>Lugar *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {LUGARES.map(lugar => (
                    <button
                      key={lugar.value}
                      type="button"
                      onClick={() => updateFormData('lugar', lugar.value)}
                      className={`p-4 border rounded-lg text-sm font-medium transition-colors duration-150 ${
                        formData.lugar === lugar.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`btn-lugar-${lugar.value}`}
                    >
                      {lugar.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                Asegúrese de que la dirección es correcta. Los enseres deben estar accesibles desde la calle.
              </div>
            </div>
          )}

          {/* Step 3: Fecha (no turno - fixed schedule 07:00-13:00) */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="fecha">Fecha Preferida de Retirada *</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      id="fecha"
                      className="w-full mt-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm text-left hover:border-gray-400 transition-colors duration-150"
                      data-testid="input-fecha"
                    >
                      <span className={formData.fecha_preferida_retirada ? 'text-gray-900' : 'text-gray-400'}>
                        {formData.fecha_preferida_retirada
                          ? new Date(formData.fecha_preferida_retirada + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                          : 'Seleccione una fecha'}
                      </span>
                      <CalendarDays className="w-4 h-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarWidget
                      mode="single"
                      selected={formData.fecha_preferida_retirada ? new Date(formData.fecha_preferida_retirada + 'T12:00:00') : undefined}
                      onSelect={(date) => {
                        if (!date) return;
                        updateFormData('fecha_preferida_retirada', formatFechaISO(date));
                        setDateOpen(false);
                      }}
                      disabled={esDiaBloqueado}
                      data-testid="calendar-fecha"
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                  <span>
                    {loadingDias
                      ? 'Comprobando disponibilidad real...'
                      : 'Los días en gris (sábados, domingos, festivos y días ya completos) no están disponibles'}
                  </span>
                </p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-800">
                  Horario de recogida: {TURNO_RECOGIDA}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  El servicio de recogida se realiza en horario de mañana (7:00 a 13:00). 
                  Por favor, asegúrese de que los enseres estén accesibles en ese horario.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Items */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {formData.items.length} de {MAX_ITEMS_PER_TICKET} items
                </span>
              </div>
              {formData.items.map((item, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">Item {index + 1}</span>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 text-sm hover:text-red-700 transition-colors duration-150"
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                  <div>
                    <Label>Tipo de Enser *</Label>
                    <select
                      value={item.tipo_enser}
                      onChange={(e) => updateItem(index, 'tipo_enser', e.target.value)}
                      className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                    >
                      {TIPOS_ENSER.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Elemento *</Label>
                    <select
                      value={item.elemento}
                      onChange={(e) => updateItem(index, 'elemento', e.target.value)}
                      className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Seleccione...</option>
                      {ELEMENTOS_COMUNES[item.tipo_enser]?.map(elem => (
                        <option key={elem} value={elem}>{elem}</option>
                      ))}
                    </select>
                  </div>
                  {item.elemento === 'Otro' && (
                    <div>
                      <Label>Especifique el elemento *</Label>
                      <Input
                        value={item.datos_item}
                        onChange={(e) => updateItem(index, 'datos_item', e.target.value)}
                        placeholder="Describa el elemento..."
                      />
                    </div>
                  )}
                </div>
              ))}
              
              {formData.items.length < MAX_ITEMS_PER_TICKET && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={addItem}
                  className="w-full"
                  data-testid="btn-add-item"
                >
                  + Añadir Item ({formData.items.length}/{MAX_ITEMS_PER_TICKET})
                </Button>
              )}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                Máximo {MAX_ITEMS_PER_TICKET} items por solicitud
              </div>
            </div>
          )}

          {/* Step 5: Resumen */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Contacto</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Nombre:</strong> {formData.nombre_ciudadano}</p>
                  <p><strong>Teléfono:</strong> {formData.telefono}</p>
                  <p><strong>Email:</strong> {formData.email}</p>
                  <p><strong>Canal preferido:</strong> {formData.canal_respuesta_preferido}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Ubicación</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Dirección:</strong> {formData.direccion}</p>
                  {formData.bloque && <p><strong>Bloque:</strong> {formData.bloque}</p>}
                  {formData.piso && <p><strong>Piso:</strong> {formData.piso}</p>}
                  <p><strong>Lugar:</strong> {formData.lugar}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Planificación</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Fecha:</strong> {formData.fecha_preferida_retirada}</p>
                  <p><strong>Horario de recogida:</strong> {TURNO_RECOGIDA}</p>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Items ({formData.items.length})</h3>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      <strong>{index + 1}.</strong> {item.tipo_enser} - {item.elemento}
                      {item.datos_item && ` (${item.datos_item})`}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="observaciones">Observaciones Adicionales (opcional)</Label>
                <textarea
                  id="observaciones"
                  value={formData.observaciones}
                  onChange={(e) => updateFormData('observaciones', e.target.value)}
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="Indique cualquier información adicional relevante..."
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              data-testid="btn-prev"
            >
              Anterior
            </Button>
            
            {currentStep < totalSteps ? (
              <Button onClick={nextStep} data-testid="btn-next">
                Siguiente
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-primary"
                data-testid="btn-submit"
              >
                {loading ? 'Enviando...' : 'Enviar Solicitud'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CitizenWizard;
